import { useState, useRef } from "react";
import "./CompleteProfile.css";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  LinearProgress,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate, useLoaderData } from "react-router-dom";
import toast from "react-hot-toast";
import { completeProfile } from "../../../services";
import { useUser } from "../../../contexts/UserContext";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const powers = [
  "No Power Required",
  "16 Amp Single Phase",
  "30 Amp Service",
  "32 Amp Single Phase",
  "50 Amp Service",
  "63 Amp 3-Phase",
  "63 Amp Single Phase",
  "100 Amp Single Phase",
  "125 Amp 3-Phase",
  "480V 3-Phase 100 amp",
  "480V 3-Phase 200 amp",
  "Hard wire 480V 3-Phase 600amp",
  "Other , please specify"
];

const steps = ["Yacht Information", "Yacht Specifications", "Captain Info", "Documents", "Payment Setup"];

export const CompleteProfile = () => {
  const navigate = useNavigate();
  const user = useLoaderData();
  const { refreshUserData } = useUser();

  const uploadVesselInsuranceRef = useRef(null);
  const uploadVesselRegistrationRef = useRef(null);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [profileCompleted, setProfileCompleted] = useState(false);
  const [insurance, setInsurance] = useState("no file chosen");
  const [insuranceFile, setInsuranceFile] = useState(null);
  const [registration, setRegistration] = useState("no file chosen");
  const [registrationFile, setRegistrationFile] = useState(null);

  // Handles the "Other" option for power required field
  const [userSpecifiedPowerRequired, setUserSpecifiedPowerRequired] = useState("");
  const [showOtherPowerRequiredTextField, setShowOtherPowerRequiredTextField] = useState(false);

  const [values, setValues] = useState({
    yachtName: "",
    imoHullNumber: "",
    yachtType: "",
    hullType: "",
    length: "",
    beam: "",
    draft: "",
    powerRequired: "",
    captainName: "",
    cellNumber: "",
    captainEmail: "",
  });

  const getValues = (target) => {
    const { name, value } = target;
    setValues({ ...values, [name]: value });

    // Only toggle the "Other" text field when powerRequired changes
    if (name === "powerRequired") {
      if (value === "Other , please specify") {
        setShowOtherPowerRequiredTextField(true);
      } else {
        setShowOtherPowerRequiredTextField(false);
        setUserSpecifiedPowerRequired(""); // Clear custom value when switching away from "Other"
      }
    }
  };

  const preventMinus = (e) => {
    if (e.code === "Minus") {
      e.preventDefault();
    }
  };

  const fileTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];

  const handleInsuranceChange = (e) => {
    const file = e.target.files[0];
    if (file === undefined) {
      return;
    }

    if (!fileTypes.includes(file.type)) {
      toast.error("Invalid file type. Only PNG, JPG, JPEG, and PDF files are allowed");
      return;
    }

    setInsuranceFile(file);
    setInsurance(file?.name);
  };

  const handleRegistrationChange = (e) => {
    const file = e.target.files[0];
    if (file === undefined) {
      return;
    }

    if (!fileTypes.includes(file.type)) {
      toast.error("Invalid file type. Only PNG, JPG, JPEG, and PDF files are allowed");
      return;
    }

    setRegistrationFile(file);
    setRegistration(file?.name);
  };

  const handleNext = () => {
    // Validate current step before moving forward
    if (activeStep === 0) {
      if (!values.yachtName || !values.imoHullNumber) {
        toast.error("Please fill in all yacht information fields");
        return;
      }
    } else if (activeStep === 1) {
      if (!values.yachtType || !values.hullType || !values.length || !values.beam || !values.draft) {
        toast.error("Please fill in all yacht specification fields");
        return;
      }
      // Validate custom power requirement if "Other" is selected
      if (showOtherPowerRequiredTextField && !userSpecifiedPowerRequired.trim()) {
        toast.error("Please specify your power requirement");
        return;
      }
    } else if (activeStep === 2) {
      if (!values.captainName || !values.cellNumber || !values.captainEmail) {
        toast.error("Please fill in all captain information fields");
        return;
      }
    }

    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleSkip = () => {
    toast.success("You can complete your profile later from the Profile page");
    navigate("/home");
  };

  const handleGoToDashboard = async () => {
    // Refresh context to update profileComplete status before navigating
    await refreshUserData();
    navigate("/home");
  };

  const handleSetupStripe = async () => {
    // Refresh context to update profileComplete status before navigating
    await refreshUserData();
    navigate("/home/payment-account");
  };

  const handleSubmit = async () => {
    if(insuranceFile === null || registrationFile === null) {
      toast.error("Please upload all required documents");
      return;
    }

    // Prepare payload
    const yachtSpecifications = {
      yachtType: values.yachtType,
      hullType: values.hullType,
      beam: values.beam,
      draft: values.draft,
      length: values.length,
    };

    const captainInfo = {
      name: values.captainName,
      cellNumber: values.cellNumber,
      email: values.captainEmail,
    };

    const payloadObj = {
      yachtName: values.yachtName,
      imoHullNumber: values.imoHullNumber,
      powerRequired: showOtherPowerRequiredTextField ? userSpecifiedPowerRequired : values.powerRequired,
      yachtSpecifications: yachtSpecifications,
      captainInfo: captainInfo,
      insurance: insuranceFile,
      registration: registrationFile,
    };
    
    // Creating the form data form the object
    const payload = new FormData();

    for(const key in payloadObj) {
      if(key === "powerRequired" || key === "yachtSpecifications" || key === "captainInfo")
        payload.append(key, JSON.stringify(payloadObj[key]));
      else
        payload.append(key, payloadObj[key]);
    }

    setLoading(true);

    const res = await completeProfile(payload, user?.token);

    if (res?.status === false) {
      setLoading(false);
      toast.error(res?.error || res?.message || "Failed to update profile");
      return;
    }

    if (res?.status === true) {
      setLoading(false);

      if(!!res?.data === false) {
        toast.success(res?.message);
        return;
      }

      toast.success(res?.message);

      // Update stored user data in localStorage/sessionStorage only
      const storedUser = JSON.parse(
        localStorage.getItem("dockingmate") || sessionStorage.getItem("dockingmate")
      );
      if (storedUser) {
        storedUser.data = { ...storedUser.data, yachtName: res?.data?.yachtName };
        if (localStorage.getItem("dockingmate")) {
          localStorage.setItem("dockingmate", JSON.stringify(storedUser));
        } else {
          sessionStorage.setItem("dockingmate", JSON.stringify(storedUser));
        }
      }

      // Move to payment setup step - DON'T refresh context yet to prevent parent re-render
      setProfileCompleted(true);
      // Reset the power required field
      setShowOtherPowerRequiredTextField(false);
      setUserSpecifiedPowerRequired("");
      
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
      return;
    }

    setLoading(false);
    toast.error("Something went wrong, please try again later");
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Tell us about your yacht
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                This information helps us match you with suitable berths
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                id="yachtName"
                name="yachtName"
                label="Yacht Name"
                size="small"
                fullWidth
                value={values.yachtName || ""}
                onChange={(e) => getValues(e.target)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                id="imoHullNumber"
                name="imoHullNumber"
                label="Registration Number" // Referred in backend as IMO/HULL Number
                size="small"
                fullWidth
                value={values.imoHullNumber || ""}
                onChange={(e) => getValues(e.target)}
              />
            </Grid>
          </Grid>
        );

      case 1:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Yacht Specifications
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                These details help ensure proper berth compatibility
              </Typography>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl>
                <FormLabel id="yacht-type-radio">Yacht Type</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="yacht-type-radio"
                  name="yachtType"
                  value={values?.yachtType}
                  onChange={(e) => getValues(e.target)}
                >
                  <FormControlLabel value="Motor" control={<Radio />} label="Motor" />
                  <FormControlLabel value="Sailing" control={<Radio />} label="Sailing" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl>
                <FormLabel id="hull-type-radio">Hull Type</FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="hull-type-radio"
                  name="hullType"
                  value={values?.hullType}
                  onChange={(e) => getValues(e.target)}
                >
                  <FormControlLabel value="Mono hull" control={<Radio />} label="Mono hull" />
                  <FormControlLabel value="Multi hull" control={<Radio />} label="Multi hull" />
                  <FormControlLabel value="Trimaran" control={<Radio />} label="Trimaran" />
                </RadioGroup>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                required
                id="length"
                name="length"
                label="Length (meters)"
                size="small"
                fullWidth
                type="number"
                value={values.length || ""}
                onChange={(e) => getValues(e.target)}
                onKeyPress={preventMinus}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                required
                id="beam"
                name="beam"
                label="Beam (meters)"
                size="small"
                fullWidth
                type="number"
                value={values.beam || ""}
                onChange={(e) => getValues(e.target)}
                onKeyPress={preventMinus}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                required
                id="draft"
                name="draft"
                label="Draft (meters)"
                size="small"
                fullWidth
                type="number"
                value={values.draft || ""}
                onChange={(e) => getValues(e.target)}
                onKeyPress={preventMinus}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl size="small" fullWidth>
                <InputLabel id="powerReq">Power Requirement</InputLabel>
                <Select
                  labelId="powerReq"
                  id="powerReqDrop"
                  fullWidth
                  name="powerRequired"
                  value={values?.powerRequired}
                  onChange={(e) => getValues(e.target)}
                  label="Power Requirement"
                >
                  {powers.map((power) => (
                    <MenuItem key={power} value={power}>
                      {power}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {showOtherPowerRequiredTextField && (
              <Grid item xs={12}>
                <TextField
                  required
                  id="userSpecifiedPowerRequired"
                  name="userSpecifiedPowerRequired"
                  label="Please specify your power requirement"
                  size="small"
                  fullWidth
                  value={userSpecifiedPowerRequired}
                  onChange={(e) => setUserSpecifiedPowerRequired(e.target.value)}
                  placeholder="e.g., 200 Amp 3-Phase"
                />
              </Grid>
            )}
          </Grid>
        );

      case 2:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Captain Information
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                We'll use this to contact you about berth bookings
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                required
                id="captainName"
                name="captainName"
                label="Captain Name"
                size="small"
                fullWidth
                value={values.captainName || ""}
                onChange={(e) => getValues(e.target)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                type="number"
                id="cellNumber"
                name="cellNumber"
                label="Cell Number"
                size="small"
                placeholder="+31 612345678"
                fullWidth
                value={values.cellNumber || ""}
                onChange={(e) => getValues(e.target)}
                onKeyPress={preventMinus}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                required
                type="email"
                id="captainEmail"
                name="captainEmail"
                label="Captain Email"
                size="small"
                fullWidth
                value={values.captainEmail || ""}
                onChange={(e) => getValues(e.target)}
              />
            </Grid>
          </Grid>
        );

       case 3:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Upload Documents
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Help us verify your identity
              </Typography>
            </Grid>
            <Grid item xs={12} className="fileContainer">
              <Button
                className="btn theme-btn"
                variant="contained"
                onClick={() => uploadVesselInsuranceRef?.current?.click()}
              >
                Upload Vessel Insurance
                <VisuallyHiddenInput
                  required
                  ref={uploadVesselInsuranceRef}
                  type="file"
                  onChange={handleInsuranceChange}
                />
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                {insurance}
              </Typography>
            </Grid>
            <Grid item xs={12} className="fileContainer">
              <Button
                className="btn theme-btn"
                variant="contained"
                onClick={() => uploadVesselRegistrationRef?.current?.click()}
              >
                Upload Vessel Registration
                <VisuallyHiddenInput
                  required
                  ref={uploadVesselRegistrationRef}
                  type="file"
                  onChange={handleRegistrationChange}
                />
              </Button>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                {registration}
              </Typography>
            </Grid>
          </Grid>
        );

      case 4:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12} sx={{ textAlign: "center" }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mb: 2,
                  color: "success.main",
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  ✓ Profile Completed Successfully!
                </Typography>
              </Box>
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                One More Step: Setup Payment Account
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                To receive payments for berth rentals and enable all listing features, you'll need to connect your Stripe account.
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Box
                sx={{
                  backgroundColor: "rgba(2, 136, 209, 0.08)",
                  border: "1px solid",
                  borderColor: "rgba(2, 136, 209, 0.3)",
                  borderRadius: 2,
                  p: 3,
                  mb: 2,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Why do I need Stripe?
                </Typography>
                <Typography variant="body2" color="text.secondary" component="div">
                  <ul style={{ marginTop: 8, paddingLeft: 20 }}>
                    <li>Receive payments securely when others rent your berth</li>
                    <li>Enable listing your berths for hire or swap</li>
                    <li>Process transactions safely through Stripe Connect</li>
                    <li>Required for berth owners who want to earn from rentals</li>
                  </ul>
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", mb: 2 }}>
                You can setup Stripe now or skip and do it later from the Payment Account page.
              </Typography>
            </Grid>
          </Grid>
        );

      default:
        return "Unknown step";
    }
  };

  const getProgressPercentage = () => {
    return ((activeStep + 1) / steps.length) * 100;
  };

  return (
    <div className="complete-profile-page">
      <Container maxWidth="md">
        <Box>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ mb: 4, textAlign: "center" }}>
                <Typography variant="h4" gutterBottom>
                  Complete Your Profile
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Help us personalize your experience
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={getProgressPercentage()}
                  sx={{ mt: 2, height: 8, borderRadius: 4 }}
                />
              </Box>

              <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              <Box sx={{ minHeight: 300 }}>
                {getStepContent(activeStep)}
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4 }}>
                {activeStep === steps.length - 1 ? (
                  // Payment Setup Step - Show centered buttons
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 2, width: "100%" }}>
                    <Button
                      variant="outlined"
                      onClick={handleGoToDashboard}
                      sx={{ minWidth: 150 }}
                    >
                      Go to Dashboard
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSetupStripe}
                      className="theme-btn"
                      sx={{ minWidth: 150 }}
                    >
                      Setup Stripe Account
                    </Button>
                  </Box>
                ) : (
                  <>
                    <Button
                      disabled={activeStep === 0 || profileCompleted}
                      onClick={handleBack}
                      sx={{ mr: 1 }}
                    >
                      Back
                    </Button>
                    <Box>
                      <Button onClick={handleSkip} sx={{ mr: 1 }}>
                        Skip for now
                      </Button>
                      {activeStep === steps.length - 2 ? (
                        <Button
                          variant="contained"
                          onClick={handleSubmit}
                          disabled={loading || profileCompleted}
                          className="theme-btn"
                        >
                          {loading ? "Completing..." : "Complete Profile"}
                        </Button>
                      ) : (
                        <Button variant="contained" onClick={handleNext} className="theme-btn">
                          Next
                        </Button>
                      )}
                    </Box>
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </div>
  );
};
